import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmDialog } from 'shared/components/AppComponents';
import { IAppState } from 'state/RootReducer';
import { useSelector } from 'react-redux';

interface ApprovalGate {
  _id: string;
  gate_name: string;
  required_artifacts: string;
  reviewers: string;
  sort_order: number;
  organizationId: string;
  created_by: string;
  updated_by: string;
  deleted_by: string | null;
  deleted_at: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface ApprovalGateCardProps {
  approvalGate: ApprovalGate;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const ApprovalGateCard = ({
  approvalGate,
  onEdit,
  onDelete,
}: ApprovalGateCardProps): React.ReactElement => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const handleCloseDelete = (): void => {
    setShowDeleteModal(false);
    setSelectedItem('');
  };

  const handleDeleteClick = (id: string): void => {
    setSelectedItem(id);
    setShowDeleteModal(true);
  };

  return (
    <>
      <Card variant="outlined" className="mt-2">
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid
              container
              spacing={1}
              key={approvalGate._id}
              onMouseEnter={() => setHoveredId(approvalGate._id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    flexWrap="wrap"
                  >
                    <Chip
                      label={`Step ${approvalGate.sort_order}`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="h6" fontWeight={700}>
                      {approvalGate.gate_name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      opacity: hoveredId === approvalGate._id ? 1 : 0,
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    {orgACLConfig.policies?.includes(
                      'policies.approvalGates.edit'
                    ) && (
                      <IconButton
                        size="small"
                        onClick={() => onEdit?.(approvalGate._id)}
                        sx={{
                          '&:hover': {
                            bgcolor: '#dbdbdb',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {orgACLConfig.policies?.includes(
                      'policies.approvalGates.delete'
                    ) && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleDeleteClick(approvalGate._id);
                        }}
                        sx={{
                          '&:hover': {
                            bgcolor: '#dbdbdb',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography color="text.secondary" variant="body1">
                    Required artifacts
                  </Typography>
                  <Typography variant="body1">
                    {approvalGate.required_artifacts}
                  </Typography>

                  <Typography color="text.secondary" variant="body1">
                    Reviewers
                  </Typography>
                  <Typography variant="body1">
                    {approvalGate.reviewers}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
      <ConfirmDialog
        title="Delete Approval Gate"
        subTitle="Are you sure you want to delete this approval gate?"
        showConfirm={showDeleteModal}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          onDelete?.(selectedItem);
          handleCloseDelete();
        }}
        onCancel={handleCloseDelete}
        onCloseDialog={handleCloseDelete}
      />
    </>
  );
};

export default ApprovalGateCard;
